import { $wrapNodes } from '@lexical/selection';
import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { useEffect, useRef } from 'react';

import { CustomParagraphNode } from '~components/generic/editable-field/components/nodes/CustomParagraphNode';

interface BlockOptionsDropdownListProps {
  editor: LexicalEditor;
  blockType: string;
  toolbarRef: React.RefObject<HTMLDivElement>;
  setShowBlockOptions: (show: boolean) => void;
  includedControls: string[];
}

export const BlockOptionsList: React.FC<BlockOptionsDropdownListProps> = ({
  editor,
  blockType,
  toolbarRef,
  setShowBlockOptions,
  includedControls,
}) => {
  const dropDownRef = useRef(null);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const dropDown = dropDownRef.current;

    if (toolbar !== null && dropDown !== null) {
      const { top, left } = toolbar.getBoundingClientRect();
      dropDown.style.top = `${top + 40}px`;
      dropDown.style.left = `${left}px`;
    }
  }, [dropDownRef, toolbarRef]);

  useEffect(() => {
    const dropDown = dropDownRef.current;
    const toolbar = toolbarRef.current;

    if (dropDown !== null && toolbar !== null) {
      // Using setTimeout to avoid immediate closing
      let timeoutId = null;

      const handle = event => {
        // Clear any pending timeout
        if (timeoutId) clearTimeout(timeoutId);

        // Set a small timeout to avoid immediate closing
        timeoutId = setTimeout(() => {
          const target = event.target;
          if (!dropDown.contains(target) && !toolbar.contains(target)) {
            setShowBlockOptions(false);
          }
        }, 100);
      };

      // Use capturing phase (true as third parameter)
      document.addEventListener('click', handle, true);

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        document.removeEventListener('click', handle, true);
      };
    }
  }, [dropDownRef, setShowBlockOptions, toolbarRef]);

  const formatHeading = (headingLevel: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        const parentNode = nodes[0].getParent();

        // Toggle behavior: if the current blockType matches the heading level we're trying to apply,
        // convert it back to a paragraph
        if (blockType === headingLevel) {
          // Converting back to a paragraph
          const existingClasses =
            parentNode instanceof CustomParagraphNode
              ? parentNode.__customClassName
                  .split(' ')
                  .filter(className => className !== 'u-size-xl')
                  .join(' ')
              : '';
          const existingStyles = parentNode instanceof CustomParagraphNode ? parentNode.__style : '';

          // Use 'p' tag to convert back to a regular paragraph
          $wrapNodes(selection, () => new CustomParagraphNode(existingClasses, existingStyles, 'p'));
        } else {
          // Apply the heading format (existing behavior)
          const existingClasses =
            parentNode instanceof CustomParagraphNode
              ? parentNode.__customClassName
                  .split(' ')
                  .filter(className => className !== 'u-size-xl')
                  .join(' ')
              : '';
          const existingStyles = parentNode instanceof CustomParagraphNode ? parentNode.__style : '';

          // Apply the requested heading level
          $wrapNodes(selection, () => new CustomParagraphNode(existingClasses, existingStyles, headingLevel));
        }
      }
    });
    setShowBlockOptions(false);
  };

  return (
    <div className="lexical-block-options-list" ref={dropDownRef}>
      {includedControls.includes('headlineOne') && (
        <button className={`item ${blockType === 'h1' ? 'active' : ''}`} onClick={() => formatHeading('h1')}>
          <span className="text">H1</span>
        </button>
      )}

      {includedControls.includes('headlineTwo') && (
        <button className={`item ${blockType === 'h2' ? 'active' : ''}`} onClick={() => formatHeading('h2')}>
          <span className="text">H2</span>
        </button>
      )}

      {includedControls.includes('headlineThree') && (
        <button className={`item ${blockType === 'h3' ? 'active' : ''}`} onClick={() => formatHeading('h3')}>
          <span className="text">H3</span>
        </button>
      )}

      {includedControls.includes('headlineFour') && (
        <button className={`item ${blockType === 'h4' ? 'active' : ''}`} onClick={() => formatHeading('h4')}>
          <span className="text">H4</span>
        </button>
      )}

      {includedControls.includes('headlineFive') && (
        <button className={`item ${blockType === 'h5' ? 'active' : ''}`} onClick={() => formatHeading('h5')}>
          <span className="text">H5</span>
        </button>
      )}

      {includedControls.includes('headlineSix') && (
        <button className={`item ${blockType === 'h6' ? 'active' : ''}`} onClick={() => formatHeading('h6')}>
          <span className="text">H6</span>
        </button>
      )}
    </div>
  );
};
